package sarka.design.skoria.ui.composables

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.Layout
import androidx.compose.ui.layout.layoutId
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.TextLayoutResult
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlin.math.ceil
import kotlin.math.max
import sarka.design.skoria.messaging.MessageView
import sarka.design.skoria.ui.theme.RCTheme

/**
 * Message list for direct conversations.
 *
 * Intentionally separate from the random chat list in AndroidCore and
 * WebDesktopCore, even though they look similar. The two have different
 * requirements: this one has stable message ids to key on, delivery and read
 * state to show, and a known person on the other end so bubble alignment carries
 * the sender rather than a "Partner:" prefix. Random chat messages are ephemeral
 * and anonymous. Sharing one composable would mean a growing pile of flags.
 */
@Composable
fun DmMessageList(
    messages: List<MessageView>,
    modifier: Modifier = Modifier,
) {
    val listState = rememberLazyListState()

    // Keyed on the last id rather than the list size, so a message being replaced
    // by a delivery-receipt update doesn't trigger a redundant scroll.
    LaunchedEffect(messages.lastOrNull()?.id) {
        if (messages.isNotEmpty()) {
            listState.animateScrollToItem(messages.lastIndex)
        }
    }

    LazyColumn(
        state = listState,
        modifier = modifier.fillMaxSize().padding(horizontal = 12.dp, vertical = 8.dp),
        verticalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        items(
            count = messages.size,
            key = { index -> messages[index].id },
        ) { index ->
            DmMessageBubble(messages[index])
        }
    }
}

@Composable
private fun DmMessageBubble(message: MessageView) {
    val cardColor = if (message.fromMe) RCTheme.colors.primary else RCTheme.colors.surfaceVariant
    val textColor = if (message.fromMe) RCTheme.colors.onPrimary else RCTheme.colors.onSurface

    BoxWithConstraints(
        modifier = Modifier.fillMaxWidth(),
        contentAlignment = if (message.fromMe) Alignment.CenterEnd else Alignment.CenterStart,
    ) {
        // Cap the bubble so long messages don't stretch edge to edge; this leaves
        // room to read the sender through alignment and keeps lines a sane length.
        val maxBubbleWidth = maxWidth * 0.85f

        Card(
            modifier = Modifier.widthIn(max = maxBubbleWidth),
            elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
            colors = CardDefaults.cardColors(containerColor = cardColor),
        ) {
            val statusLabel = if (message.fromMe) {
                when {
                    message.read -> "Read"
                    message.delivered -> "Delivered"
                    else -> "Sent"
                }
            } else {
                null
            }

            TextWithTrailingStatus(
                text = message.text,
                textColor = textColor,
                status = statusLabel,
                statusColor = textColor.copy(alpha = 0.7f),
                modifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp),
            )
        }
    }
}

/**
 * Lays out message text with a small status label that hugs the trailing edge.
 *
 * The label sits on the same line as the last word when there's room for it,
 * and drops to its own line (right-aligned, flush to the text's right edge)
 * only when the last line is too full. This mirrors how mainstream chat apps
 * place their timestamp/receipt, and avoids both the stranded-label problem of
 * FlowRow and the always-on-its-own-line bulk of a plain Column.
 */
@Composable
private fun TextWithTrailingStatus(
    text: String,
    textColor: Color,
    status: String?,
    statusColor: Color,
    modifier: Modifier = Modifier,
) {
    // Horizontal gap between the end of the last text line and the label.
    val gapPx = with(LocalDensity.current) { 8.dp.roundToPx() }

    var textLayout by remember { mutableStateOf<TextLayoutResult?>(null) }

    Layout(
        modifier = modifier,
        content = {
            SelectionContainer(modifier = Modifier.layoutId("text")) {
                Text(
                    text = text,
                    color = textColor,
                    onTextLayout = { textLayout = it },
                )
            }
            if (status != null) {
                Text(
                    modifier = Modifier.layoutId("status"),
                    text = status,
                    color = statusColor,
                    fontSize = 10.sp,
                )
            }
        },
    ) { measurables, constraints ->
        val textMeasurable = measurables.first { it.layoutId == "text" }
        val statusMeasurable = measurables.firstOrNull { it.layoutId == "status" }

        val textPlaceable = textMeasurable.measure(constraints)

        if (statusMeasurable == null) {
            // No status label (messages from the other person): plain text.
            return@Layout layout(textPlaceable.width, textPlaceable.height) {
                textPlaceable.place(0, 0)
            }
        }

        val statusPlaceable = statusMeasurable.measure(
            constraints.copy(minWidth = 0, minHeight = 0),
        )

        val layout = textLayout
        val lastLineRight = if (layout != null && layout.lineCount > 0) {
            ceil(layout.getLineRight(layout.lineCount - 1)).toInt()
        } else {
            // First composition before onTextLayout fires: assume the label needs
            // its own line. It gets corrected on the next frame once we have metrics.
            textPlaceable.width
        }

        val maxWidth = constraints.maxWidth
        val neededInline = lastLineRight + gapPx + statusPlaceable.width
        val fitsInline = neededInline <= maxWidth

        if (fitsInline) {
            // Label rides on the last line, baseline-ish aligned to the bottom of
            // the text so it reads as trailing the final word.
            val width = max(textPlaceable.width, neededInline)
            val height = textPlaceable.height
            layout(width, height) {
                textPlaceable.place(0, 0)
                statusPlaceable.place(
                    x = width - statusPlaceable.width,
                    y = height - statusPlaceable.height,
                )
            }
        } else {
            // Not enough room on the last line: drop the label to its own line,
            // right-aligned to the text's actual right edge (no floating gap).
            val width = textPlaceable.width
            val height = textPlaceable.height + statusPlaceable.height
            layout(width, height) {
                textPlaceable.place(0, 0)
                statusPlaceable.place(
                    x = width - statusPlaceable.width,
                    y = textPlaceable.height,
                )
            }
        }
    }
}
